import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiAlertTriangle,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiDollarSign,
  FiEdit2,
  FiFileText,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiTrash2,
  FiTrendingDown,
  FiTrendingUp,
  FiTruck,
  FiRepeat,
  FiX,
} from 'react-icons/fi';
import { Input, Button, Alert } from '../components/common/index.js';
import { expensesApi } from '../api/expensesApi';
import expensesAxiosInstance from '../api/expensesAxiosInstance';
import { API_ENDPOINTS } from '../config/api.config';
import OpsLayout from '../components/layout/OpsLayout.jsx';
import {
  addExpense,
  clearExpensesError,
  deleteExpense,
  fetchTodayExpenses,
  updateExpense,
} from '../redux/slices/expensesSlice';

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';
};

const errorMessage = (error) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (error.detail || error.message) return error.detail || error.message;
  return 'Something went wrong';
};

const toTitleCase = (str) =>
  str.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

// ── Title Cell — autocomplete over past titles AND what's owed ────────────────
// Typing "t" offers "Tea" (a title used before); typing "ch" offers the actual
// chit from the Money section, with what's due on it. Picking a money row links
// the expense to that commitment so paying it runs its balance down.
const TitleCell = ({ id, value, onChange, onPickMoney, disabled }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOut = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    try {
      const { data } = await expensesApi.getTitleSuggestions(q);
      const list = data?.suggestions || [];
      if (list.length) { setSuggestions(list); setOpen(true); }
      else { setSuggestions([]); setOpen(false); }
    } catch { setSuggestions([]); }
  }, []);

  const handleChange = (e) => {
    onChange(e.target.value);
    onPickMoney(null);            // typing over a picked chit unlinks it
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 250);
  };

  const choose = (s) => {
    onChange(s.title);
    onPickMoney(s.kind === 'money' ? s : null);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => value.trim() && fetchSuggestions(value)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        disabled={disabled}
        placeholder="Enter title…"
        autoComplete="off"
        className="w-full h-10 px-3 bg-transparent text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:bg-violet-50/70 disabled:opacity-50 transition-colors"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 top-full mt-0.5 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1">
          {suggestions.map((s) => (
            <li
              key={`${s.kind}-${s.money_id || s.title}`}
              onMouseDown={() => choose(s)}
              className="cursor-pointer px-3 py-2 transition-colors hover:bg-violet-50"
            >
              {s.kind === 'money' ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-bold text-slate-800">{s.title}</span>
                      <span className="shrink-0 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-px text-[9px] font-bold text-violet-700">
                        {s.category_label || 'Money'}
                      </span>
                      {s.overdue && (
                        <span className="shrink-0 rounded-full bg-rose-50 px-1.5 py-px text-[9px] font-bold text-rose-600">
                          overdue
                        </span>
                      )}
                    </div>
                    <p className="truncate text-[11px] text-slate-400">{s.schedule_text}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-black text-rose-600">₹{formatAmount(s.due_amount)}</p>
                    <p className="text-[10px] font-semibold text-slate-400">to pay</p>
                  </div>
                </div>
              ) : (
                <span className="text-sm text-slate-700">{s.title}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Title Autocomplete — labeled, for the Edit modal ──────────────────────────
const TitleAutocomplete = ({ id = 'exp-title', value, onChange, error, disabled }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOut = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    try {
      const { data } = await expensesApi.getCategories(q);
      if (Array.isArray(data) && data.length) { setSuggestions(data); setOpen(true); }
      else { setSuggestions([]); setOpen(false); }
    } catch { setSuggestions([]); }
  }, []);

  const handleChange = (e) => {
    onChange(e);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 300);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <label htmlFor={id} className="block text-sm font-semibold text-slate-700 mb-2">
        Title <span className="text-rose-500">*</span>
      </label>
      <input
        id={id}
        type="text"
        placeholder="e.g. Petrol, Office supplies"
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        disabled={disabled}
        autoComplete="off"
        className={`w-full px-4 py-3 border rounded-xl text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 ${
          error
            ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
            : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      />
      {error && <p className="text-rose-500 text-xs mt-1.5 font-medium">{error}</p>}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
          {suggestions.map((t) => (
            <li
              key={t}
              onMouseDown={() => { onChange({ target: { value: t } }); setSuggestions([]); setOpen(false); }}
              className="px-4 py-2.5 text-sm text-slate-700 cursor-pointer hover:bg-violet-50 hover:text-violet-700 transition-colors"
            >
              {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Spreadsheet row ────────────────────────────────────────────────────────────
const SheetRow = ({ idx, row, rowError, disabled, onUpdate, onPickMoney }) => {
  const m = row.money;
  const due = m ? Number(m.due_amount) : 0;
  const paying = Number(row.amount) || 0;
  // Paying less than the due is an ADVANCE — the rest stays owed on Money.
  const isAdvance = Boolean(m) && paying > 0 && paying < due;
  const leftAfter = m ? Math.max(Number(m.remaining) - paying, 0) : 0;

  return (
    <div className={`border-b border-slate-100 last:border-b-0 ${m ? 'bg-violet-50/40' : idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}`}>
      <div className="grid grid-cols-[36px_1fr_130px]">
        <div className="flex items-center justify-center border-r border-slate-100 text-xs font-bold text-slate-400 select-none py-px">
          {idx + 1}
        </div>
        <div className={`border-r border-slate-100 ${rowError?.title ? 'bg-rose-50/60' : ''}`}>
          <TitleCell
            id={`sr-${idx}`}
            value={row.title}
            onChange={(v) => onUpdate('title', v)}
            onPickMoney={onPickMoney}
            disabled={disabled}
          />
          {rowError?.title && <p className="px-3 pb-1 text-[10px] text-rose-500 leading-none">{rowError.title}</p>}
        </div>
        <div className={rowError?.amount ? 'bg-rose-50/60' : ''}>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            step="any"
            value={row.amount}
            onChange={(e) => onUpdate('amount', e.target.value)}
            disabled={disabled}
            placeholder="0.00"
            className="w-full h-10 px-3 bg-transparent text-sm text-slate-800 placeholder-slate-300 text-right focus:outline-none focus:bg-violet-50/70 disabled:opacity-50 transition-colors"
          />
          {rowError?.amount && <p className="px-3 pb-1 text-[10px] text-rose-500 text-right leading-none">{rowError.amount}</p>}
        </div>
      </div>

      {/* Linked to the Money section — pay it all, or an advance */}
      {m && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-violet-100 px-3 py-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[10px] font-bold text-violet-700">
              {m.category_label || 'Money'}
            </span>
            <span className="text-[11px] font-semibold text-slate-500">
              ₹{formatAmount(m.due_amount)} due
              {m.period_label ? ` · ${m.period_label}` : ''}
              {m.next_due_display && !m.period_label ? ` · by ${m.next_due_display}` : ''}
            </span>
            {m.overdue && (
              <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">overdue</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onUpdate('amount', String(due))}
              className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition ${!isAdvance && paying === due ? 'border-violet-500 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              Pay full ₹{formatAmount(due)}
            </button>
            <span className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold ${isAdvance ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-400'}`}>
              {isAdvance ? `Advance · ₹${formatAmount(leftAfter)} left` : 'Advance'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Add Expense Modal — spreadsheet style ──────────────────────────────────────
const DEFAULT_ROWS = 10;
// `money` is set when the title was picked from the Money section — the row
// then pays that chit/bill down instead of just logging a loose expense.
const EMPTY_ROW = () => ({
  id: String(Date.now() + Math.random()), title: '', amount: '', money: null,
});

const AddExpenseModal = ({ submitting, apiError, onSubmit, onClose }) => {
  const [rows, setRows] = useState(() => Array.from({ length: DEFAULT_ROWS }, EMPTY_ROW));
  const [errors, setErrors] = useState({});
  const [batchNotes, setBatchNotes] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    if (errors[idx]?.[field]) setErrors((prev) => ({ ...prev, [idx]: { ...prev[idx], [field]: '' } }));
  };

  // Picking a chit/bill prefills the FULL due — the common case is paying it
  // off; the admin can type a smaller number to pay an advance/part instead.
  const pickMoney = (idx, money) => {
    setRows((prev) => prev.map((r, i) => (
      i === idx
        ? { ...r, money, amount: money ? String(Number(money.due_amount)) : r.amount }
        : r
    )));
    setErrors((prev) => ({ ...prev, [idx]: { ...prev[idx], amount: '', title: '' } }));
  };

  const filledRows = rows.filter((r) => r.title.trim() && r.amount && Number(r.amount) > 0);
  const liveTotal = filledRows.reduce((sum, r) => sum + Number(r.amount), 0);

  const validate = () => {
    const next = {};
    rows.forEach((row, idx) => {
      if (!row.title.trim() && !row.amount) return;
      const rowErr = {};
      if (!row.title.trim()) rowErr.title = 'Required';
      if (!row.amount) rowErr.amount = 'Required';
      else if (Number(row.amount) <= 0) rowErr.amount = '> 0';
      // Paying more than a chit still owes would push it past settled.
      else if (row.money && Number(row.amount) > Number(row.money.remaining)) {
        rowErr.amount = `Max ₹${formatAmount(row.money.remaining)}`;
      }
      if (Object.keys(rowErr).length) next[idx] = rowErr;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate() || !filledRows.length) return;
    onSubmit({
      notes: batchNotes.trim(),
      // Rows linked to the Money section are PAID against it (which writes its
      // own Expense); the rest are plain expenses.
      moneyPayments: filledRows.filter((r) => r.money).map((r) => ({
        money_id: r.money.money_id,
        title: r.title.trim(),
        amount: String(r.amount),
      })),
      expenses: filledRows.filter((r) => !r.money).map((r) => ({
        title: toTitleCase(r.title.trim()),
        amount: String(r.amount),
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm cursor-default" onClick={onClose} aria-label="Close" />
      <div
        className="relative w-full max-w-xl rounded-3xl bg-white shadow-2xl animate-fade-in-up flex flex-col overflow-hidden"
        style={{ maxHeight: 'min(92vh, 760px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              Add Expenses
              {filledRows.length > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold px-1.5">
                  {filledRows.length}
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Empty rows are ignored — fill as many as needed</p>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Column headers */}
          <div className="grid grid-cols-[36px_1fr_130px] bg-slate-50 border-b border-slate-200 shrink-0">
            <div className="flex items-center justify-center py-2.5 border-r border-slate-200 text-[10px] font-bold text-slate-400 uppercase tracking-wider">#</div>
            <div className="flex items-center py-2.5 px-3 border-r border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Title</div>
            <div className="flex items-center justify-end py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Amount ₹</div>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1">
            {errorMessage(apiError) && (
              <div className="px-4 pt-3"><Alert type="error" message={errorMessage(apiError)} /></div>
            )}

            {/* Table */}
            <div className="border-b border-slate-100">
              {rows.map((row, idx) => (
                <SheetRow
                  key={row.id}
                  idx={idx}
                  row={row}
                  rowError={errors[idx]}
                  disabled={submitting}
                  onUpdate={(field, value) => updateRow(idx, field, value)}
                  onPickMoney={(money) => pickMoney(idx, money)}
                />
              ))}
            </div>

            {/* Batch notes */}
            <div className="px-4 py-4">
              <label htmlFor="batch-notes" className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                Notes <span className="font-normal normal-case text-slate-400">(optional — applies to the whole batch)</span>
              </label>
              <textarea
                id="batch-notes"
                value={batchNotes}
                onChange={(e) => setBatchNotes(e.target.value)}
                disabled={submitting}
                placeholder="e.g. Fuel for delivery, tea for office team"
                rows={2}
                className={`w-full px-4 py-3 rounded-xl border text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400 resize-none ${submitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60 shrink-0">
            <div className="flex-1 min-w-0">
              {filledRows.length > 0 ? (
                <p className="text-sm font-bold text-slate-700">
                  {filledRows.length} {filledRows.length === 1 ? 'entry' : 'entries'} · ₹{formatAmount(liveTotal)}
                </p>
              ) : (
                <p className="text-sm text-slate-400">Fill rows to see total</p>
              )}
            </div>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={filledRows.length === 0}>
              {filledRows.length === 0 ? 'Save' : `Save ${filledRows.length}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Edit Expense Modal ─────────────────────────────────────────────────────────
const EditExpenseModal = ({ expense, updating, apiError, onSubmit, onClose }) => {
  const [form, setForm] = useState({ title: expense.title, amount: String(expense.amount) });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = 'Required';
    if (!form.amount) next.amount = 'Required';
    else if (Number(form.amount) <= 0) next.amount = 'Must be > 0';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const patch = {};
    if (form.title.trim() !== expense.title) patch.title = toTitleCase(form.title.trim());
    if (String(form.amount) !== String(expense.amount)) patch.amount = String(form.amount);
    if (!Object.keys(patch).length) { onClose(); return; }
    onSubmit(patch);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm cursor-default" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl animate-fade-in-up overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">Edit Expense</h2>
            <p className="text-xs text-slate-400 mt-0.5">Only today's expenses can be changed</p>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage(apiError) && <Alert type="error" message={errorMessage(apiError)} />}

          <TitleAutocomplete
            id="edit-exp-title"
            value={form.title}
            onChange={handleChange('title')}
            error={errors.title}
            disabled={updating}
          />

          <Input
            label="Amount"
            name="edit-amount"
            type="number"
            inputMode="numeric"
            min="0"
            step="any"
            placeholder="e.g. 500"
            value={form.amount}
            onChange={handleChange('amount')}
            error={errors.amount}
            required
            disabled={updating}
          />

          <div className="flex gap-3 pt-1">
            <Button type="button" variant="secondary" onClick={onClose} disabled={updating} className="flex-1">Cancel</Button>
            <Button type="submit" variant="primary" loading={updating} className="flex-1">Save Changes</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Expense Row ────────────────────────────────────────────────────────────────
const ExpenseRow = ({ expense, index, onEdit, onDelete, confirmDeleteId, deleting }) => {
  const isConfirming = confirmDeleteId === expense.id;
  const isDeleting = deleting && isConfirming;

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 font-bold text-sm">
        {index + 1}
      </div>
      <p className="flex-1 min-w-0 text-sm font-semibold text-slate-900 truncate">{expense.title}</p>
      <p className="text-base font-bold text-emerald-600 shrink-0">₹{formatAmount(expense.amount)}</p>

      {isConfirming ? (
        <div className="flex items-center gap-2 shrink-0">
          {isDeleting ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-rose-400 border-t-transparent" />
          ) : (
            <>
              <span className="text-xs text-slate-500">Delete?</span>
              <button onClick={() => onDelete(expense.id, true)} className="text-xs font-bold text-rose-600 hover:text-rose-700 transition-colors">Yes</button>
              <button onClick={() => onDelete(null)} className="text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors">No</button>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(expense)}
            aria-label="Edit"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-violet-50 hover:text-violet-600 transition-colors"
          >
            <FiEdit2 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(expense.id, false)}
            aria-label="Delete"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-colors"
          >
            <FiTrash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
// ── Money to pay — every payable in one place ─────────────────────────────────
// The admin plans the day's spending here, so "what must go out" can't live in
// four different screens. Customer refunds, OSM vendor balances, planned
// payments (salary/rent/EMI/chit) and chit-money repayments all land in one
// list, on a cumulative Today → This week → This month window. Anything past
// its date is overdue and shows in EVERY window — money owed never hides.
const PAYABLE_PERIODS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
];

const SOURCE_META = {
  returns: { icon: FiRotateCcw, cls: 'bg-amber-50 text-amber-600' },
  osm: { icon: FiTruck, cls: 'bg-sky-50 text-sky-600' },
  // Chit, borrow, salary and rent all come from the one Money section
  money: { icon: FiRepeat, cls: 'bg-violet-50 text-violet-600' },
};

const PayablesPanel = () => {
  const [period, setPeriod] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await expensesApi.getPayables(period);
      setData(res.data ?? res);
      setError('');
    } catch {
      setError('Could not load what needs to be paid');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items ?? [];
  const upcoming = data?.upcoming ?? [];
  const sources = data?.sources ?? {};
  const overdue = Number(data?.overdue_count) || 0;

  return (
    <div className="rounded-3xl border border-rose-200 bg-white shadow-sm animate-fade-in-up">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
            <FiTrendingDown className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">Money to pay</p>
            <p className="text-xs text-slate-500">
              Refunds, OSM vendors, planned payments &amp; chit — all together
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-black text-rose-600">₹{formatAmount(data?.total ?? 0)}</p>
            <p className="text-[11px] font-semibold text-slate-400">
              {data ? `${data.count} to pay · ${data.period_label?.toLowerCase()}` : '—'}
            </p>
            {data && Number(data.outstanding_total) > Number(data.total) && (
              <p className="text-[11px] font-semibold text-slate-400">
                ₹{formatAmount(data.outstanding_total)} owed in total
              </p>
            )}
          </div>
          {open ? <FiChevronUp className="h-5 w-5 text-slate-400" /> : <FiChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4">
          {error && <div className="mb-3"><Alert type="error" message={error} /></div>}

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {PAYABLE_PERIODS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriod(p.value)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${period === p.value ? 'border-rose-500 bg-rose-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-rose-300'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
              aria-label="Refresh what needs to be paid"
              title="Refresh"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {overdue > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5">
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-700">
                <FiAlertTriangle className="h-4 w-4" /> ₹{formatAmount(data.overdue_total)} overdue
              </span>
              <span className="text-xs font-medium text-rose-600/80">
                {overdue} payment{overdue === 1 ? '' : 's'} past the due date
              </span>
            </div>
          )}

          {/* What the total is made of. Each card shows what's due in this
              window AND what's owed overall — a chit due next week is still
              real money owed while the admin is looking at Today. */}
          <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {Object.entries(sources).map(([key, s]) => {
              const meta = SOURCE_META[key] || SOURCE_META.returns;
              const Icon = meta.icon;
              const later = Number(s.outstanding_total) - Number(s.total);
              return (
                <div key={key} className={`rounded-xl border p-3 ${Number(s.outstanding_count) ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/60'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <p className="truncate text-[11px] font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
                  </div>
                  <p className={`mt-1.5 text-base font-black ${Number(s.count) ? 'text-slate-900' : 'text-slate-300'}`}>
                    ₹{formatAmount(s.total)}
                  </p>
                  <p className="text-[11px] font-semibold text-slate-400">
                    {s.count} due {data?.period_label?.toLowerCase()}
                  </p>
                  {later > 0 && (
                    <p className="mt-1 border-t border-slate-100 pt-1 text-[11px] font-semibold text-violet-600">
                      +₹{formatAmount(later)} later · ₹{formatAmount(s.outstanding_total)} owed
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 py-10 text-center">
              <FiCheck className="mx-auto mb-2 h-7 w-7 text-emerald-200" />
              <p className="text-sm text-slate-400">Nothing to pay {data?.period_label?.toLowerCase()}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => {
                const meta = SOURCE_META[it.source] || SOURCE_META.returns;
                const Icon = meta.icon;
                return (
                  <div
                    key={it.id}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3.5 py-3 ${it.overdue ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200 bg-white'}`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${meta.cls}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="truncate text-sm font-bold text-slate-900">{it.title}</p>
                          <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                            {it.source_label}
                          </span>
                        </div>
                        {it.subtitle && <p className="truncate text-xs text-slate-500">{it.subtitle}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-black text-slate-900">₹{formatAmount(it.amount)}</p>
                        <p className={`text-[11px] font-bold ${it.overdue ? 'text-rose-600' : 'text-slate-400'}`}>
                          {it.overdue ? `overdue · ${it.due_display}` : `due ${it.due_display}`}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Owed, but not due in this window — so the chit and the rest of the
              money picture never disappear behind the period filter. */}
          {!loading && upcoming.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Coming after {data?.period_label?.toLowerCase()} · ₹
                {formatAmount(Number(data.outstanding_total) - Number(data.total))}
              </p>
              <div className="space-y-1.5">
                {upcoming.map((it) => {
                  const meta = SOURCE_META[it.source] || SOURCE_META.returns;
                  const Icon = meta.icon;
                  return (
                    <div key={it.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3.5 py-2.5">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.cls} opacity-70`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-slate-600">{it.title}</p>
                          {it.subtitle && <p className="truncate text-[11px] text-slate-400">{it.subtitle}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-slate-600">₹{formatAmount(it.amount)}</p>
                        <p className="text-[11px] font-semibold text-slate-400">due {it.due_display}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Cash refunds to pay ───────────────────────────────────────────────────────
// Cash returns are money owed to customers. They land here so admin, while
// planning the day's expenses at night, sees exactly who to pay and how much,
// and marks each Paid once handed over. Exchange returns never appear.
const CashRefundsPanel = () => {
  const [refunds, setRefunds] = useState([]);
  const [totalPending, setTotalPending] = useState('0.00');
  const [pendingCount, setPendingCount] = useState(0);
  const [view, setView] = useState('pending'); // 'pending' | 'paid'
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (which = view) => {
    setLoading(true);
    try {
      const { data } = await expensesApi.getCashRefunds({ status: which });
      setRefunds(data?.refunds || []);
      setTotalPending(data?.total_pending ?? '0.00');
      setPendingCount(data?.pending_count ?? 0);
      setError('');
    } catch {
      setError('Could not load cash refunds');
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(view); }, [load, view]);

  const markPaid = async (id, paid) => {
    setBusyId(id);
    try {
      await expensesApi.markCashRefundPaid(id, paid);
      await load(view);
    } catch {
      setError('Could not update the refund');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-3xl border border-amber-200 bg-white shadow-sm animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <FiRotateCcw className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">Cash refunds to pay</p>
            <p className="text-[11px] text-slate-400">Money owed to customers from returns</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-black leading-none text-amber-600">₹{formatAmount(totalPending)}</p>
            <p className="mt-0.5 text-[11px] text-slate-400">{pendingCount} pending</p>
          </div>
          {open ? <FiChevronUp className="h-4 w-4 text-slate-400" /> : <FiChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4">
          <div className="mb-3 flex items-center gap-1.5">
            {[{ v: 'pending', l: 'To pay' }, { v: 'paid', l: 'Paid' }].map((t) => (
              <button
                key={t.v}
                onClick={() => setView(t.v)}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${view === t.v ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {t.l}
              </button>
            ))}
            <button
              onClick={() => load(view)}
              disabled={loading}
              className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Refresh cash refunds"
            >
              <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {error && <p className="mb-2 text-xs font-semibold text-rose-500">{error}</p>}

          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
            </div>
          ) : refunds.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">
              {view === 'pending' ? 'No cash refunds pending — all settled 🎉' : 'No refunds paid yet'}
            </p>
          ) : (
            <div className="space-y-2">
              {refunds.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {r.customer_name || 'Customer'}
                      {r.customer_phone && <span className="ml-1.5 text-xs font-normal text-slate-400">{r.customer_phone}</span>}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {r.product_name} · {r.car}
                      {r.return_status === 'pending' && <span className="ml-1 text-amber-500">· return not yet completed</span>}
                    </p>
                    {r.cash_paid_at && <p className="text-[11px] text-emerald-500">Paid {r.cash_paid_at.split(' at ')[0]}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-black text-slate-900">₹{formatAmount(r.amount)}</span>
                    {view === 'pending' ? (
                      <button
                        onClick={() => markPaid(r.id, true)}
                        disabled={busyId === r.id}
                        className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        <FiCheck className="h-3.5 w-3.5" /> Paid
                      </button>
                    ) : (
                      <button
                        onClick={() => markPaid(r.id, false)}
                        disabled={busyId === r.id}
                        className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-white disabled:opacity-50"
                      >
                        Undo
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ExpensesPage = () => {
  const dispatch = useDispatch();
  const {
    date, notes, list, totalAmount, count,
    loading, submitting, updating, deleting,
    error, updateError, deleteError,
  } = useSelector((s) => s.expenses);

  const [showAdd, setShowAdd] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [day, setDay] = useState(null);   // /admin/ops/day/ — collections side
  const [moneyError, setMoneyError] = useState('');   // paying a chit/bill failed

  const loadDay = useCallback(() => {
    expensesAxiosInstance
      .get(API_ENDPOINTS.OPS.DAY)
      .then((res) => setDay(res.data))
      .catch(() => {});   // money card is additive — the sheet still works without it
  }, []);

  useEffect(() => { dispatch(fetchTodayExpenses()); }, [dispatch]);
  useEffect(() => { loadDay(); }, [loadDay]);

  // Balance in hand updates LIVE as expenses are typed in: collections come
  // from the day summary, the expense total from the redux sheet state.
  const netIn = Number(day?.collections?.net_in);
  const liveBalance = Number.isFinite(netIn) ? netIn - (Number(totalAmount) || 0) : null;

  const closeAdd = useCallback(() => {
    setShowAdd(false);
    setMoneyError('');
    dispatch(clearExpensesError());
  }, [dispatch]);

  const handleAdd = async ({ moneyPayments = [], expenses = [], notes }) => {
    // Money rows are PAID against their chit/bill — that writes its own Expense
    // and runs the balance down. Loose rows are plain expenses.
    setMoneyError('');
    try {
      for (const p of moneyPayments) {
        await expensesApi.payMoney(p.money_id, { amount: p.amount, note: notes || undefined });
      }
    } catch (e) {
      setMoneyError(e?.response?.data?.error || 'Could not record the payment against Money');
      return;
    }
    if (expenses.length) {
      const result = await dispatch(addExpense({ notes, expenses }));
      if (result.error) return;
    }
    if (moneyPayments.length) {
      dispatch(fetchTodayExpenses());   // the money payments added their own rows
      loadDay();
    }
    closeAdd();
  };

  const closeEdit = useCallback(() => {
    setEditingExpense(null);
    dispatch(clearExpensesError());
  }, [dispatch]);

  const handleEdit = async (patch) => {
    const result = await dispatch(updateExpense({ id: editingExpense.id, data: patch }));
    if (!result.error) closeEdit();
  };

  const handleDelete = async (id, confirmed) => {
    if (id === null) { setConfirmDeleteId(null); return; }
    if (!confirmed) { setConfirmDeleteId(id); return; }
    await dispatch(deleteExpense(id));
    setConfirmDeleteId(null);
  };

  return (
    <OpsLayout>
      <div className="space-y-5">

        {/* The two day cards side by side: what was spent | what's in hand */}
        <div className="grid gap-5 lg:grid-cols-2">

        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-6 text-white shadow-lg shadow-violet-500/20 animate-fade-in-up">
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-300">Today's Expenses</p>
          <p className="mt-1 text-2xl font-black">{date || '—'}</p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="flex items-center gap-1.5 text-xs text-violet-200"><FiFileText className="h-3.5 w-3.5" /> Total entries</p>
              <p className="mt-1 text-2xl font-black">{loading ? '—' : count}</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="flex items-center gap-1.5 text-xs text-violet-200"><FiDollarSign className="h-3.5 w-3.5" /> Total amount</p>
              <p className="mt-1 text-2xl font-black">₹{loading ? '—' : formatAmount(totalAmount)}</p>
            </div>
          </div>
        </div>

        {/* Today's money — what came in, what went out, what's in hand.
            The balance moves LIVE as expenses are added below. */}
        {day?.collections && (
          <div className="flex flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.02s' }}>
            <div className="flex items-center justify-between">
              <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900">
                <FiTrendingUp className="h-4 w-4 text-emerald-500" /> Today's money
              </p>
              <button
                onClick={loadDay}
                className="text-xs font-semibold text-slate-400 hover:text-violet-600"
              >
                Refresh
              </button>
            </div>
            <div className="mt-3 space-y-1.5 text-sm">
              {[
                ['Sales payments (full)', day.collections.sales_full],
                ['Advances received', day.collections.advances],
                ['Balances collected', day.collections.balances],
                ['Counter sales', day.collections.counter],
              ].filter(([, v]) => Number(v) > 0).map(([label, v]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-semibold text-slate-800">₹{formatAmount(v)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
                <span className="font-bold text-slate-700">Money in</span>
                <span className="font-bold text-emerald-600">₹{formatAmount(day.collections.total_in)}</span>
              </div>
              {Number(day.collections.refunds_paid) > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Refunds paid out</span>
                  <span className="font-semibold text-rose-500">− ₹{formatAmount(day.collections.refunds_paid)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Expenses so far</span>
                <span className="font-semibold text-rose-500">− ₹{formatAmount(totalAmount)}</span>
              </div>
            </div>
            <div className={`mt-auto pt-3`}>
              <div className={`flex items-center justify-between rounded-2xl px-4 py-3 ${liveBalance != null && liveBalance < 0 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                <span className={`text-sm font-bold ${liveBalance != null && liveBalance < 0 ? 'text-rose-700' : 'text-emerald-800'}`}>Balance in hand</span>
                <span className={`text-xl font-black ${liveBalance != null && liveBalance < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {liveBalance != null ? `₹${formatAmount(liveBalance)}` : '—'}
                </span>
              </div>
            </div>
          </div>
        )}

        </div>

        {/* Batch notes */}
        {notes && (
          <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.04s' }}>
            <FiFileText className="h-4 w-4 shrink-0 mt-0.5 text-violet-400" />
            <p className="text-sm text-slate-600 leading-relaxed">{notes}</p>
          </div>
        )}

        {/* Cash refunds owed to customers (from cash returns) */}
        <PayablesPanel />

        <CashRefundsPanel />

        {/* Actions bar */}
        <div className="flex items-center justify-between gap-3 animate-fade-in-up" style={{ animationDelay: '0.06s' }}>
          <h2 className="text-base font-bold text-slate-900">
            {loading && !list.length ? 'Loading…' : `${count} expense${count !== 1 ? 's' : ''} today`}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { dispatch(fetchTodayExpenses()); loadDay(); }}
              disabled={loading}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50 transition-colors"
              aria-label="Refresh"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-500/20 hover:from-violet-700 hover:to-indigo-700 active:scale-95 transition-all"
            >
              <FiPlus className="h-4 w-4" />
              Add Expense
            </button>
          </div>
        </div>

        {/* Errors */}
        {error && !showAdd && !editingExpense && <Alert type="error" message={errorMessage(error)} />}
        {updateError && !editingExpense && <Alert type="error" message={errorMessage(updateError)} />}
        {deleteError && <Alert type="error" message={errorMessage(deleteError)} />}

        {/* List */}
        <div className="space-y-2 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {loading && !list.length ? (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            </div>
          ) : list.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white py-16 text-center shadow-sm">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50">
                <FiFileText className="h-6 w-6 text-violet-400" />
              </div>
              <p className="font-semibold text-slate-700">No expenses today</p>
              <p className="mt-1 text-sm text-slate-400">Tap "Add Expense" to record your first one.</p>
            </div>
          ) : (
            list.map((expense, i) => (
              <ExpenseRow
                key={expense.id}
                expense={expense}
                index={i}
                onEdit={setEditingExpense}
                onDelete={handleDelete}
                confirmDeleteId={confirmDeleteId}
                deleting={deleting}
              />
            ))
          )}
        </div>
      </div>

      {showAdd && (
        <AddExpenseModal
          submitting={submitting}
          apiError={moneyError || error}
          onSubmit={handleAdd}
          onClose={closeAdd}
        />
      )}

      {editingExpense && (
        <EditExpenseModal
          expense={editingExpense}
          updating={updating}
          apiError={updateError}
          onSubmit={handleEdit}
          onClose={closeEdit}
        />
      )}
    </OpsLayout>
  );
};

export default ExpensesPage;
